import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {MessageSquare, ArrowLeft, MapPin, Mail, Briefcase, Store, UserPlus, UserCheck, Clock as ClockIcon, Image as ImageIcon, Package, Euro, User, UserRound, Users, Rss, FileText, Flame, GlassWater, UtensilsCrossed, Wine, SprayCan} from 'lucide-react';
import { lumi } from '@/lib/lumi';
import { useAuth } from '@/hooks/useAuth';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

// ─── Social Icons ─────────────────────────────────────────────────────────────
function InstagramIcon({ size = 20 }: {size?: number;}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
    </svg>);
}
function TwitterIcon({ size = 20 }: {size?: number;}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>);
}
function FacebookIcon({ size = 20 }: {size?: number;}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>);
}
function YoutubeIcon({ size = 20 }: {size?: number;}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>);
}
function LinkedinIcon({ size = 20 }: {size?: number;}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>);
}

type Setor = 'churrasqueiro' | 'barman' | 'cameriere' | 'sommelier' | 'limpeza' | 'recepcionista';

const SETOR_LABELS: Record<string, { label: string; icon: React.ReactNode }> = {
  churrasqueiro: { label: 'Churrasqueiro', icon: <Flame size={14} /> },
  barman: { label: 'Barman', icon: <GlassWater size={14} /> },
  cameriere: { label: 'Cameriere', icon: <UtensilsCrossed size={14} /> },
  sommelier: { label: 'Sommelier', icon: <Wine size={14} /> },
  limpeza: { label: 'Limpeza', icon: <SprayCan size={14} /> },
  recepcionista: { label: 'Recepcionista', icon: <UserRound size={14} /> }
};

const BANNER_IMAGES: Record<Setor, string> = {
  churrasqueiro: 'https://static.lumi.new/a5/a5b94dabfd63af6f12a5b858ed874941.jpg',
  barman: 'https://static.lumi.new/95/956c6d4c238260fa5bc32201a10454cb.jpg',
  cameriere: 'https://static.lumi.new/68/6865937459df61b418dd0181a2c65d1d.jpg',
  sommelier: 'https://static.lumi.new/bb/bbc0eac46b59fe7c39b38c4778b1c89f.jpg',
  limpeza: 'https://static.lumi.new/dd/dd84d1a94fc13fd5961d869f39aa9ff9.jpg',
  recepcionista: 'https://static.lumi.new/58/583e18c7f414e5e10477480c7fb9ca64.jpg'
};

interface SocialLinks {
  twitter?: string;
  facebook?: string;
  instagram?: string;
  youtube?: string;
  linkedin?: string;
}

interface PerfilUsuario {
  _id?: string;
  userId: string;
  nome?: string;
  email?: string;
  profileImageUrl?: string;
  bannerUrl?: string;
  profileType?: string;
  cidade?: string;
  setor?: string;
  biografia?: string;
  albumPhotos?: string[];
  socialLinks?: SocialLinks;
  areasAtuacao?: string[];
}

type StatusAmizade =
  | 'nenhum'
  | 'pendente_enviada'
  | 'pendente_recebida'
  | 'amigos'
  | 'proprio';

type PublicTab = 'perfil' | 'album' | 'marketplace' | 'amigos' | 'feed' | 'curriculo';

function PerfilPublico() {
  const { usuarioId } = useParams<{ usuarioId: string }>();
  const { user, isAuthenticated } = useAuth();

  const [perfil, setPerfil] = useState<PerfilUsuario | null>(null);
  const [anuncios, setAnuncios] = useState<any[]>([]);
  const [amigos, setAmigos] = useState<any[]>([]);
  const [feedPosts, setFeedPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusAmizade, setStatusAmizade] = useState<StatusAmizade>('nenhum');
  const [enviandoAmizade, setEnviandoAmizade] = useState(false);
  const [activeTab, setActiveTab] = useState<PublicTab>('perfil');

  // Carregar dados do perfil do usuario clicado
  useEffect(() => {
    const carregar = async () => {
      if (!usuarioId) return;
      setLoading(true);
      try {
        const { list } = await lumi.entities.user_profiles.list({
          filter: { userId: usuarioId },
          limit: 1
        });
        if (list && list.length > 0) {
          setPerfil(list[0] as PerfilUsuario);
        } else {
          setPerfil(null);
        }

        // Anuncios do usuario
        const { list: anunciosList } = await lumi.entities.marketplace_anuncios.list({
          filter: { userId: usuarioId },
          sort: { createdAt: -1 }
        });
        setAnuncios(anunciosList || []);

        // Amigos do usuario
        const { list: amizadesList } = await lumi.entities.amizades.list({ sort: { createdAt: -1 } });
        const aceitas = (amizadesList || []).filter((a: any) =>
          a.status === 'aceito' &&
          (a.solicitanteId === usuarioId || a.destinatarioId === usuarioId)
        );
        const amigosList = aceitas.map((a: any) => {
          const souSolicitante = a.solicitanteId === usuarioId;
          return {
            usuarioId: souSolicitante ? a.destinatarioId : a.solicitanteId,
            nome: souSolicitante ? a.destinatarioNome : a.solicitanteNome,
            foto: souSolicitante ? a.destinatarioFoto : a.solicitanteFoto
          };
        });
        setAmigos(amigosList);

        // Feed do usuario (posts proprios)
        const { list: postsList } = await lumi.entities.posts_feed.list({ sort: { createdAt: -1 } });
        const meusPosts = (postsList || []).filter((p: any) => p.userId === usuarioId);
        setFeedPosts(meusPosts);
      } catch (e) {
        console.error('Erro ao carregar perfil:', e);
        setPerfil(null);
      } finally {
        setLoading(false);
      }
    };
    carregar();
  }, [usuarioId]);

  // Verificar status de amizade
  useEffect(() => {
    const verificarAmizade = async () => {
      if (!usuarioId || !user?.userId) return;
      if (usuarioId === user.userId) {
        setStatusAmizade('proprio');
        return;
      }
      try {
        const { list } = await lumi.entities.amizades.list({ sort: { createdAt: -1 } });
        const relacao = (list || []).find((a: any) =>
          (a.solicitanteId === user.userId && a.destinatarioId === usuarioId) ||
          (a.solicitanteId === usuarioId && a.destinatarioId === user.userId)
        );
        if (!relacao) {
          setStatusAmizade('nenhum');
        } else if (relacao.status === 'aceito') {
          setStatusAmizade('amigos');
        } else if (relacao.solicitanteId === user.userId) {
          setStatusAmizade('pendente_enviada');
        } else {
          setStatusAmizade('pendente_recebida');
        }
      } catch (e) {
        console.error('Erro ao verificar amizade:', e);
      }
    };
    verificarAmizade();
  }, [usuarioId, user?.userId]);

  const handleFazerAmizade = async () => {
    if (!usuarioId || !user?.userId || !perfil) return;
    setEnviandoAmizade(true);
    try {
      const now = new Date().toISOString();
      let solicitanteFoto = '';
      let solicitanteBanner = '';
      try {
        const { list: meuPerfil } = await lumi.entities.user_profiles.list({ filter: { userId: user.userId }, limit: 1 });
        if (meuPerfil && meuPerfil.length > 0) {
          solicitanteFoto = meuPerfil[0].profileImageUrl || '';
          solicitanteBanner = meuPerfil[0].bannerUrl || '';
        }
      } catch (e) {/* segue sem foto */}

      await lumi.entities.amizades.create({
        solicitanteId: user.userId,
        solicitanteNome: user.userName || user.email || 'Usuario',
        solicitanteFoto,
        solicitanteBanner,
        destinatarioId: usuarioId,
        destinatarioNome: nomeExibicao,
        destinatarioFoto: perfil.profileImageUrl || '',
        destinatarioBanner: perfil.bannerUrl || '',
        status: 'pendente',
        creator: user.userId,
        createdAt: now,
        updatedAt: now
      });
      setStatusAmizade('pendente_enviada');
    } catch (e) {
      console.error('Erro ao enviar solicitacao de amizade:', e);
    } finally {
      setEnviandoAmizade(false);
    }
  };

  const getImagemAnuncio = (anuncio: any): string => {
    if (anuncio?.fotos && anuncio.fotos.length > 0) return anuncio.fotos[0];
    if (anuncio?.imagem) return anuncio.imagem;
    return 'https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?w=800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1a1a]">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-20 flex items-center justify-center">
          <div className="w-10 h-10 border-4 border-[#E53935] border-t-transparent rounded-full animate-spin" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!perfil) {
    return (
      <div className="min-h-screen bg-[#1a1a1a]">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-[#2a2a2a] flex items-center justify-center mx-auto mb-5">
              <User className="w-9 h-9 text-gray-600" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">Perfil não encontrado</h2>
            <p className="text-gray-500 mb-6">Este usuário ainda não possui um perfil disponível.</p>
            <Link
              to="/buscar-usuarios"
              className="inline-flex items-center gap-2 text-[#E53935] hover:underline font-semibold"
            >
              <ArrowLeft className="w-5 h-5" />
              Voltar à pesquisa
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const isRestaurante = (perfil.profileType || 'trabalhador') === 'restaurante';
  const nomeExibicao = perfil.nome || perfil.email?.split('@')[0] || 'Usuário';
  const inicial = nomeExibicao.charAt(0).toUpperCase();
  const setorKey = (perfil.setor && BANNER_IMAGES[perfil.setor as Setor]) ? (perfil.setor as Setor) : 'churrasqueiro';
  const bannerSrc = perfil.bannerUrl || BANNER_IMAGES[setorKey];

  const socialItems = [
    { key: 'twitter' as const, label: 'X (Twitter)', icon: <TwitterIcon size={22} /> },
    { key: 'facebook' as const, label: 'Facebook', icon: <FacebookIcon size={22} /> },
    { key: 'instagram' as const, label: 'Instagram', icon: <InstagramIcon size={22} /> },
    { key: 'youtube' as const, label: 'YouTube', icon: <YoutubeIcon size={22} /> },
    { key: 'linkedin' as const, label: 'LinkedIn', icon: <LinkedinIcon size={22} /> }
  ];
  const links: SocialLinks = perfil.socialLinks || {};
  const temAlgumLink = socialItems.some((i) => links[i.key]);

  const tabs: { key: PublicTab; label: string; icon: React.ReactNode }[] = [
    { key: 'perfil', label: 'Perfil', icon: <UserRound size={15} /> },
    { key: 'album', label: 'Album', icon: <ImageIcon size={15} /> },
    { key: 'marketplace', label: 'Marketplace', icon: <Package size={15} /> },
    { key: 'amigos', label: 'Amigos', icon: <Users size={15} /> },
    { key: 'feed', label: 'Feed', icon: <Rss size={15} /> },
    ...(isRestaurante ? [] : [{ key: 'curriculo' as PublicTab, label: 'Curriculo', icon: <FileText size={15} /> }])
  ];

  return (
    <div className="min-h-screen bg-[#1a1a1a] flex flex-col">
      <Navbar />

      {/* Banner */}
      <div className="relative h-[360px] md:h-[460px] overflow-hidden">
        <img src={bannerSrc} alt="Banner" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a] via-black/40 to-transparent" />
        <div className="absolute top-5 left-0 right-0">
          <div className="w-[92%] lg:w-[82%] mx-auto">
            <Link
              to="/buscar-usuarios"
              className="inline-flex items-center gap-2 bg-black/50 backdrop-blur-md text-gray-200 hover:text-white px-4 py-2 rounded-xl font-semibold transition-colors border border-white/10"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar à pesquisa
            </Link>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="w-[92%] lg:w-[82%] mx-auto -mt-20 relative z-10 pb-16">

        {/* Profile header card */}
        <div className="bg-[#2a2a2a] rounded-2xl border border-[#3a3a3a] shadow-2xl overflow-visible mb-1 relative">
          {/* Avatar sobressaindo o card */}
          <div className="absolute left-8 sm:left-10 -top-[80px] z-20">
            {perfil.profileImageUrl ? (
              <img
                src={perfil.profileImageUrl}
                alt={nomeExibicao}
                className="w-[140px] h-[140px] sm:w-[160px] sm:h-[160px] rounded-full object-cover border-[5px] border-[#2a2a2a] shadow-[0_8px_32px_rgba(0,0,0,0.5)] ring-2 ring-[#E53935]/40"
              />
            ) : (
              <div className="w-[140px] h-[140px] sm:w-[160px] sm:h-[160px] rounded-full bg-gradient-to-br from-[#C62828] via-[#E53935] to-[#FF5722] flex items-center justify-center text-white text-5xl sm:text-6xl font-black border-[5px] border-[#2a2a2a] shadow-[0_8px_32px_rgba(0,0,0,0.5)] ring-2 ring-[#E53935]/40">
                {inicial}
              </div>
            )}
            <div className="absolute top-2 right-2 w-4 h-4 bg-green-500 rounded-full border-[3px] border-[#2a2a2a] shadow-md" />
          </div>

          <div className="px-6 sm:px-10 pt-5 sm:pt-6 pb-5">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
              <div className="flex-1 text-center sm:text-left min-w-0 sm:pl-[180px]">
                <h1 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">{nomeExibicao}</h1>
                <div className="flex items-center justify-center sm:justify-start gap-1.5 text-sm text-gray-400 mt-1">
                  {isRestaurante ? (
                    <><Store size={14} className="text-[#E53935]" /> <span>Estabelecimento</span></>
                  ) : (
                    <><Briefcase size={14} className="text-[#E53935]" /> <span>Trabalhador</span></>
                  )}
                </div>
                <div className="flex items-center justify-center sm:justify-start gap-2 mt-2 text-gray-500 text-sm">
                  <MapPin size={14} className="text-[#E53935]" />
                  <span>{perfil.cidade || 'Milao, Italia'}</span>
                </div>
                {!isRestaurante && SETOR_LABELS[setorKey] && (
                  <div className="mt-2.5 flex justify-center sm:justify-start">
                    <span className="inline-flex items-center gap-1.5 bg-[#E53935]/10 text-[#E53935] text-xs font-semibold px-3.5 py-1.5 rounded-full border border-[#E53935]/20">
                      {SETOR_LABELS[setorKey].icon}
                      {SETOR_LABELS[setorKey].label}
                    </span>
                  </div>
                )}
              </div>

              {/* Acoes */}
              <div className="flex flex-wrap gap-3 shrink-0 mt-2 sm:mt-0 items-center justify-center">
                {isAuthenticated && user?.userId !== perfil.userId && perfil.userId && (
                  <Link to={`/perfil?tab=chat&contato=${encodeURIComponent(perfil.userId)}`} className="inline-flex items-center gap-2 bg-[#E53935] hover:bg-[#C62828] text-white px-5 py-2.5 rounded-xl font-semibold text-sm">
                    <MessageSquare size={18} /> Enviar mensagem
                  </Link>
                )}
                {perfil.email && (
                  <a
                    href={`mailto:${perfil.email}`}
                    className="inline-flex items-center gap-2 bg-[#3a3a3a] border border-[#4a4a4a] hover:bg-[#4a4a4a] text-gray-200 hover:text-white px-4 py-2.5 rounded-xl font-medium transition-all text-sm"
                  >
                    <Mail className="w-4 h-4 text-[#E53935]" />
                    Contato
                  </a>
                )}
                {isAuthenticated && statusAmizade !== 'proprio' && (
                  statusAmizade === 'nenhum' ? (
                    <button
                      onClick={handleFazerAmizade}
                      disabled={enviandoAmizade}
                      className="inline-flex items-center gap-2 bg-gradient-to-r from-[#C62828] to-[#E53935] text-white px-5 py-2.5 rounded-xl font-medium transition-all hover:shadow-lg hover:shadow-[#E53935]/30 disabled:opacity-60 disabled:cursor-not-allowed text-sm"
                    >
                      {enviandoAmizade ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <UserPlus className="w-4 h-4" />
                      )}
                      {enviandoAmizade ? 'Enviando...' : 'Fazer amizade'}
                    </button>
                  ) : statusAmizade === 'pendente_enviada' ? (
                    <span className="inline-flex items-center gap-2 bg-[#3a3a3a] border border-[#E57335]/40 text-[#E57335] px-5 py-2.5 rounded-xl font-medium text-sm">
                      <ClockIcon className="w-4 h-4" />
                      Solicitação enviada
                    </span>
                  ) : statusAmizade === 'pendente_recebida' ? (
                    <span className="inline-flex items-center gap-2 bg-[#3a3a3a] border border-[#E57335]/40 text-[#E57335] px-5 py-2.5 rounded-xl font-medium text-sm">
                      <ClockIcon className="w-4 h-4" />
                      Aguardando sua resposta
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-2 bg-green-900/30 border border-green-600/40 text-green-400 px-5 py-2.5 rounded-xl font-medium text-sm">
                      <UserCheck className="w-4 h-4" />
                      Amigos
                    </span>
                  )
                )}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-t border-[#3a3a3a] overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 min-w-[120px] flex items-center justify-center gap-2 py-3.5 text-sm font-semibold transition-all border-b-2 ${
                  activeTab === tab.key
                    ? 'border-[#E53935] text-[#E53935] bg-[#E53935]/5'
                    : 'border-transparent text-gray-500 hover:text-gray-300 hover:bg-[#333]'
                }`}
              >
                {tab.icon}
                {tab.label}
                {tab.key === 'album' && Array.isArray(perfil.albumPhotos) && perfil.albumPhotos.length > 0 && (
                  <span className="text-xs text-gray-500 ml-1">({perfil.albumPhotos.length})</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* TAB: PERFIL */}
        {activeTab === 'perfil' && (
          <div className="bg-[#2a2a2a] rounded-2xl border border-[#3a3a3a] shadow-xl p-6 sm:p-9 space-y-8 mt-1">
            {/* Sobre mim */}
            <div>
              <h2 className="text-base font-bold text-white flex items-center gap-2.5 mb-4">
                <div className="w-1 h-5 rounded-full bg-[#E53935]" />
                Sobre mim
              </h2>
              <p className="text-gray-400 leading-relaxed text-sm whitespace-pre-wrap">
                {perfil.biografia || 'Este usuário ainda não adicionou uma biografia.'}
              </p>
            </div>

            {/* Funcoes / Areas de Atuacao */}
            {Array.isArray(perfil.areasAtuacao) && perfil.areasAtuacao.length > 0 && (
              <div>
                <h2 className="text-base font-bold text-white flex items-center gap-2.5 mb-4">
                  <div className="w-1 h-5 rounded-full bg-[#E53935]" />
                  <Briefcase size={16} className="text-[#E53935]" />
                  Funções
                </h2>
                <div className="flex flex-wrap gap-2.5">
                  {perfil.areasAtuacao.map((funcao, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center gap-1.5 bg-[#3a2a2a] text-[#E57335] text-sm font-semibold px-4 py-2 rounded-xl border border-[#E53935]/30 shadow-sm"
                    >
                      <Flame size={14} />
                      {funcao}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Redes Sociais */}
            <div>
              <h2 className="text-base font-bold text-white flex items-center gap-2.5 mb-4">
                <div className="w-1 h-5 rounded-full bg-[#8D6E63]" />
                Redes Sociais
              </h2>
              {temAlgumLink ? (
                <div className="flex items-center gap-4 flex-wrap">
                  {socialItems.map((item) => {
                    const link = links[item.key];
                    return link ? (
                      <a
                        key={item.key}
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-[43px] h-[43px] rounded-full bg-[#5D4037] flex items-center justify-center text-white hover:scale-125 transition-all duration-300 shadow-md hover:shadow-xl hover:shadow-[#5D4037]/60 hover:-translate-y-1"
                        title={item.label}
                      >
                        <span className="[&_svg]:w-[22px] [&_svg]:h-[22px] [&_svg]:fill-white">{item.icon}</span>
                      </a>
                    ) : null;
                  })}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">Este usuário ainda não vinculou redes sociais.</p>
              )}
            </div>
          </div>
        )}

        {/* TAB: ALBUM */}
        {activeTab === 'album' && (
          <div className="bg-[#2a2a2a] rounded-2xl border border-[#3a3a3a] shadow-xl p-6 sm:p-9 mt-1">
            <h2 className="text-base font-bold text-white flex items-center gap-2.5 mb-5">
              <div className="w-1 h-5 rounded-full bg-[#8D6E63]" />
              <ImageIcon size={18} className="text-[#8D6E63]" />
              Álbum de fotos
            </h2>
            {Array.isArray(perfil.albumPhotos) && perfil.albumPhotos.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {perfil.albumPhotos.map((foto, index) => (
                  <div key={index} className="aspect-square rounded-xl overflow-hidden border border-[#3a3a3a] shadow-lg">
                    <img src={foto} alt={`Foto ${index + 1}`} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 border-2 border-dashed border-[#3a3a3a] rounded-2xl">
                <ImageIcon size={36} className="text-gray-600 mb-3" />
                <p className="text-gray-400 text-base font-semibold mb-1">Álbum vazio</p>
                <p className="text-gray-600 text-sm">Este usuário ainda não adicionou fotos ao álbum.</p>
              </div>
            )}
          </div>
        )}

        {/* TAB: MARKETPLACE */}
        {activeTab === 'marketplace' && (
          <div className="bg-[#2a2a2a] rounded-2xl border border-[#3a3a3a] shadow-xl p-6 sm:p-9 mt-1">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-base font-bold text-white flex items-center gap-2.5">
                <div className="w-1 h-5 rounded-full bg-[#E53935]" />
                <Package size={18} className="text-[#E53935]" />
                Anúncios no Marketplace
                {anuncios.length > 0 && <span className="text-[#E53935]">({anuncios.length})</span>}
              </h2>
              {anuncios.length > 0 && (
                <Link
                  to={`/marketplace/usuario/${usuarioId}`}
                  className="text-[#E53935] text-sm font-semibold hover:underline"
                >
                  Ver todos
                </Link>
              )}
            </div>
            {anuncios.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {anuncios.slice(0, 6).map((anuncio) => (
                  <div
                    key={anuncio._id}
                    className="bg-[#1e1e1e] border border-[#3a3a3a] rounded-xl overflow-hidden hover:border-[#E53935] transition-all group"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={getImagemAnuncio(anuncio)}
                        alt={anuncio.titulo}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      {anuncio.categoria && (
                        <div className="absolute top-2 right-2 bg-[#E53935] text-white px-2.5 py-1 rounded-full text-xs font-bold capitalize">
                          {anuncio.categoria}
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="text-white font-bold text-base mb-1 line-clamp-1">{anuncio.titulo}</h3>
                      <p className="text-gray-500 text-xs mb-3 line-clamp-2">{anuncio.descricao}</p>
                      <div className="flex items-center justify-between pt-3 border-t border-[#333]">
                        <span className="flex items-center gap-1.5 text-gray-500 text-xs">
                          <MapPin size={11} /> {anuncio.localizacao}
                        </span>
                        <span className="flex items-center text-[#E57335] font-black text-lg">
                          <Euro size={15} />{anuncio.preco}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 border-2 border-dashed border-[#3a3a3a] rounded-2xl">
                <Package size={36} className="text-gray-600 mb-3" />
                <p className="text-gray-400 text-base font-semibold mb-1">Nenhum anúncio</p>
                <p className="text-gray-600 text-sm">Este usuário ainda não publicou anúncios.</p>
              </div>
            )}
          </div>
        )}

        {/* TAB: AMIGOS */}
        {activeTab === 'amigos' && (
          <div className="bg-[#2a2a2a] rounded-2xl border border-[#3a3a3a] shadow-xl p-6 sm:p-9 mt-1">
            <h2 className="text-base font-bold text-white flex items-center gap-2.5 mb-5">
              <div className="w-1 h-5 rounded-full bg-[#E53935]" />
              <Users size={18} className="text-[#E53935]" />
              Amigos
              {amigos.length > 0 && <span className="text-[#E53935]">({amigos.length})</span>}
            </h2>
            {amigos.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {amigos.map((amigo: any, idx: number) => (
                  <Link
                    key={idx}
                    to={`/perfil/${amigo.usuarioId}`}
                    className="bg-[#1e1e1e] border border-[#3a3a3a] rounded-2xl p-4 flex items-center gap-3 hover:border-[#E53935] transition-all group"
                  >
                    <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-[#333] bg-gradient-to-br from-[#C62828] to-[#E53935] flex items-center justify-center shrink-0">
                      {amigo.foto ? (
                        <img src={amigo.foto} alt={amigo.nome} className="w-full h-full object-cover" />
                      ) : (
                        <User size={24} className="text-white" />
                      )}
                    </div>
                    <h4 className="text-white font-bold text-sm truncate group-hover:text-[#E53935] transition-colors">
                      {amigo.nome || 'Usuario'}
                    </h4>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 border-2 border-dashed border-[#3a3a3a] rounded-2xl">
                <Users size={36} className="text-gray-600 mb-3" />
                <p className="text-gray-400 text-base font-semibold mb-1">Sem amigos ainda</p>
                <p className="text-gray-600 text-sm">Este usuário ainda não tem amizades.</p>
              </div>
            )}
          </div>
        )}

        {/* TAB: FEED */}
        {activeTab === 'feed' && (
          <div className="bg-[#2a2a2a] rounded-2xl border border-[#3a3a3a] shadow-xl p-6 sm:p-9 mt-1">
            <h2 className="text-base font-bold text-white flex items-center gap-2.5 mb-5">
              <div className="w-1 h-5 rounded-full bg-[#E53935]" />
              <Rss size={18} className="text-[#E53935]" />
              Feed
            </h2>
            {feedPosts.length > 0 ? (
              <div className="space-y-4 max-w-2xl mx-auto">
                {feedPosts.map((post: any) => (
                  <div key={post._id} className="bg-[#1e1e1e] border border-[#3a3a3a] rounded-2xl p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-11 h-11 rounded-full overflow-hidden border-2 border-[#333] bg-gradient-to-br from-[#C62828] to-[#E53935] flex items-center justify-center shrink-0">
                        {post.userFoto ? (
                          <img src={post.userFoto} alt={post.userNome} className="w-full h-full object-cover" />
                        ) : (
                          <User size={22} className="text-white" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-white font-bold text-sm truncate">{post.userNome || nomeExibicao}</h4>
                        <span className="text-gray-500 text-[11px]">
                          {post.createdAt ? new Date(post.createdAt).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : ''}
                        </span>
                      </div>
                    </div>
                    {post.conteudo && (
                      <p className="text-gray-300 text-sm leading-relaxed mb-3 whitespace-pre-wrap">{post.conteudo}</p>
                    )}
                    {Array.isArray(post.imagens) && post.imagens.length > 0 && (
                      <div className={`grid gap-2 ${post.imagens.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                        {post.imagens.map((img: string, i: number) => (
                          <img key={i} src={img} alt="" className="w-full rounded-xl object-cover max-h-72 border border-[#3a3a3a]" />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 border-2 border-dashed border-[#3a3a3a] rounded-2xl">
                <Rss size={36} className="text-gray-600 mb-3" />
                <p className="text-gray-400 text-base font-semibold mb-1">Nenhuma publicação</p>
                <p className="text-gray-600 text-sm">Este usuário ainda não publicou nada no feed.</p>
              </div>
            )}
          </div>
        )}

        {/* TAB: CURRICULO */}
        {activeTab === 'curriculo' && !isRestaurante && (
          <div className="bg-[#2a2a2a] rounded-2xl border border-[#3a3a3a] shadow-xl p-6 sm:p-9 mt-1">
            <h2 className="text-base font-bold text-white flex items-center gap-2.5 mb-5">
              <div className="w-1 h-5 rounded-full bg-[#E53935]" />
              <FileText size={18} className="text-[#E53935]" />
              Currículo
            </h2>
            <div className="flex flex-col sm:flex-row gap-6 items-center sm:items-start mb-6 text-center sm:text-left">
              {perfil.profileImageUrl ? (
                <img src={perfil.profileImageUrl} alt={nomeExibicao} className="w-24 h-24 rounded-full object-cover border-4 border-[#E53935]/50" />
              ) : (
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#C62828] to-[#E53935] flex items-center justify-center text-white text-3xl font-black border-4 border-[#E53935]/50">
                  {inicial}
                </div>
              )}
              <div>
                <h3 className="text-2xl font-black text-white">{nomeExibicao}</h3>
                {SETOR_LABELS[setorKey] && (
                  <p className="text-[#E57335] font-semibold mt-1">{SETOR_LABELS[setorKey].label}</p>
                )}
                <div className="flex items-center justify-center sm:justify-start gap-2 mt-2 text-gray-500 text-sm">
                  <MapPin size={14} className="text-[#E53935]" /> {perfil.cidade || 'Milao, Italia'}
                </div>
              </div>
            </div>
            {perfil.biografia && (
              <div className="bg-[#1a1a1a] p-4 rounded-xl border border-[#333]">
                <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">{perfil.biografia}</p>
              </div>
            )}
            <p className="text-gray-500 text-xs mt-4">
              Para solicitar o currículo completo, entre em contato com este usuário.
            </p>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}

export default PerfilPublico;
